import { useState, useRef, useEffect } from "react";
import { ChevronDown, Globe } from "lucide-react";

const ALL_LANGUAGES = [
  { code: "ru", name: "RU", fullName: "Русский" },
  { code: "en", name: "EN", fullName: "English" },
  { code: "tr", name: "TR", fullName: "Türkçe" },
];

export const LanguageSelector = ({ selectedLang, onLangChange, isScrolled, isMobile = false, availableLanguages }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  const languages = availableLanguages
    ? ALL_LANGUAGES.filter((l) => availableLanguages.includes(l.code))
    : ALL_LANGUAGES.filter((l) => l.code !== "tr");
  const current = languages.find((l) => l.code === selectedLang) || languages[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (isMobile) {
    return (
      <div className="border-t border-gray-100 pt-2 mt-1">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="py-3 px-4 text-left text-gray-700 hover:bg-pink-50 hover:text-pink-600 rounded-xl transition-colors flex items-center gap-2 font-semibold w-full"
          data-testid="lang-selector-mobile"
        >
          <Globe className="w-4 h-4" />
          <span>{current.fullName}</span>
          <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>
        {isOpen && (
          <div className="ml-4 border-l-2 border-pink-200 pl-2">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => { onLangChange(lang.code); setIsOpen(false); }}
                className={`py-2 px-4 text-left rounded-xl transition-colors flex items-center gap-2.5 w-full text-sm ${
                  lang.code === selectedLang
                    ? "text-pink-600 bg-pink-50 font-semibold"
                    : "text-gray-600 hover:bg-pink-50 hover:text-pink-600"
                }`}
                data-testid={`lang-option-mobile-${lang.code}`}
              >
                {lang.fullName}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-full transition-all text-sm font-semibold ${
          isScrolled
            ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
            : "bg-white/20 text-white hover:bg-white/30"
        }`}
        data-testid="lang-selector"
      >
        <Globe className="w-3.5 h-3.5" />
        <span>{current.name}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden min-w-[130px] animate-fade-in z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => { onLangChange(lang.code); setIsOpen(false); }}
              className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2.5 ${
                lang.code === selectedLang
                  ? "text-pink-600 bg-pink-50"
                  : "text-gray-700 hover:bg-gray-50 hover:text-pink-600"
              }`}
              data-testid={`lang-option-${lang.code}`}
            >
              {lang.fullName}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
